import {
  FileAttachment,
  FileCategory,
  UploadFileRequest,
  ApiResponse,
  PaginatedResponse,
  PaginationParams
} from '../../types/api'
import { apiClient, createFormData, getTenantApiUrl, getAuthToken } from '../../utils/api'

export class FileService {
  private readonly basePath = '/files'

  // File Upload
  async uploadFile(data: UploadFileRequest): Promise<ApiResponse<FileAttachment>> {
    const formData = createFormData({
      file: data.file,
      category: data.category,
      invoice_id: data.invoice_id
    })
    
    return apiClient.post(`${this.basePath}/upload`, formData)
  }

  async uploadMultipleFiles(data: {
    files: File[]
    category: FileCategory
    invoice_id?: string
  }): Promise<ApiResponse<{
    uploaded: FileAttachment[]
    failed: Array<{ file: string, error: string }>
  }>> {
    const formData = new FormData()
    
    data.files.forEach((file, index) => {
      formData.append(`files[${index}]`, file)
    })
    formData.append('category', data.category)
    
    if (data.invoice_id) {
      formData.append('invoice_id', data.invoice_id)
    }
    
    return apiClient.post(`${this.basePath}/upload-multiple`, formData)
  }

  // File Management
  async getFiles(filters?: {
    category?: FileCategory
    file_type?: string
    invoice_id?: string
    search?: string
  }, pagination?: PaginationParams): Promise<ApiResponse<PaginatedResponse<FileAttachment>>> {
    const params = {
      ...filters,
      ...pagination
    }
    return apiClient.get(`${this.basePath}`, params)
  }

  async getFile(id: string): Promise<ApiResponse<FileAttachment>> {
    return apiClient.get(`${this.basePath}/${id}`)
  }

  async deleteFile(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`${this.basePath}/${id}`)
  }

  async updateFile(id: string, data: {
    name?: string
    category?: FileCategory
  }): Promise<ApiResponse<FileAttachment>> {
    return apiClient.put(`${this.basePath}/${id}`, data)
  }

  // File Operations
  async downloadFile(id: string): Promise<Response> {
    const response = await fetch(`${getTenantApiUrl()}${this.basePath}/${id}/download`, {
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`
      }
    })
    
    if (!response.ok) {
      throw new Error('Download failed')
    }
    
    return response
  }

  async getFileUrl(id: string, expires?: number): Promise<ApiResponse<{ url: string, expires_at?: string }>> {
    return apiClient.get(`${this.basePath}/${id}/url`, { expires })
  }

  async getPreview(id: string): Promise<ApiResponse<{
    can_preview: boolean
    preview_url?: string
    preview_type?: 'image' | 'pdf' | 'text' | 'video'
  }>> {
    return apiClient.get(`${this.basePath}/${id}/preview`)
  }

  // File Processing
  async resizeImage(id: string, options: {
    width?: number
    height?: number
    quality?: number
    format?: 'jpeg' | 'png' | 'webp'
  }): Promise<ApiResponse<FileAttachment>> {
    return apiClient.post(`${this.basePath}/${id}/resize`, options)
  }

  async optimizeImage(id: string, quality: number = 80): Promise<ApiResponse<FileAttachment>> {
    return apiClient.post(`${this.basePath}/${id}/optimize`, { quality })
  }

  async convertToPdf(id: string): Promise<ApiResponse<FileAttachment>> {
    return apiClient.post(`${this.basePath}/${id}/convert-pdf`)
  }

  // Bulk Operations
  async bulkDelete(ids: string[]): Promise<ApiResponse<{
    deleted: number
    failed: Array<{ id: string, error: string }>
  }>> {
    return apiClient.delete(`${this.basePath}/bulk`, { ids })
  }

  async bulkMove(ids: string[], category: FileCategory): Promise<ApiResponse<{
    moved: number
    failed: Array<{ id: string, error: string }>
  }>> {
    return apiClient.put(`${this.basePath}/bulk/move`, { ids, category })
  }

  async bulkDownload(ids: string[]): Promise<Response> {
    const response = await fetch(`${getTenantApiUrl()}${this.basePath}/bulk/download`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify({ ids })
    })
    
    if (!response.ok) {
      throw new Error('Bulk download failed')
    }
    
    return response
  }

  // File Categories
  async getCategories(): Promise<ApiResponse<Array<{
    category: FileCategory
    count: number
    total_size: number
  }>>> {
    return apiClient.get(`${this.basePath}/categories`)
  }

  async getCategoryStatistics(category: FileCategory): Promise<ApiResponse<{
    total_files: number
    total_size: number
    file_types: Record<string, number>
    recent_uploads: FileAttachment[]
  }>> {
    return apiClient.get(`${this.basePath}/categories/${category}/statistics`)
  }

  // File Search
  async searchFiles(query: string, filters?: {
    category?: FileCategory
    file_type?: string
    size_min?: number
    size_max?: number
    date_from?: string
    date_to?: string
  }): Promise<ApiResponse<FileAttachment[]>> {
    return apiClient.get(`${this.basePath}/search`, { q: query, ...filters })
  }

  // Storage Management
  async getStorageStatistics(): Promise<ApiResponse<{
    total_files: number
    total_size: number
    used_space: number
    available_space: number
    quota: number
    quota_percentage: number
    by_category: Record<FileCategory, { count: number, size: number }>
    by_type: Record<string, { count: number, size: number }>
  }>> {
    return apiClient.get(`${this.basePath}/storage/statistics`)
  }

  async cleanupExpiredFiles(): Promise<ApiResponse<{
    deleted: number
    freed_space: number
  }>> {
    return apiClient.post(`${this.basePath}/storage/cleanup`)
  }

  // File Sharing
  async shareFile(id: string, options: {
    expires_at?: string
    password?: string
    allow_download?: boolean
    allow_preview?: boolean
  }): Promise<ApiResponse<{
    share_url: string
    share_token: string
    expires_at?: string
  }>> {
    return apiClient.post(`${this.basePath}/${id}/share`, options)
  }

  async getSharedFile(token: string, password?: string): Promise<ApiResponse<{
    file: FileAttachment
    can_download: boolean
    can_preview: boolean
  }>> {
    return apiClient.get(`${this.basePath}/shared/${token}`, { password })
  }

  async revokeShare(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`${this.basePath}/${id}/share`)
  }

  // File Validation
  async validateFile(file: File): Promise<ApiResponse<{
    valid: boolean
    errors: string[]
    warnings: string[]
    size_human: string
    type: string
  }>> {
    const formData = new FormData()
    formData.append('file', file)
    
    return apiClient.post(`${this.basePath}/validate`, formData)
  }

  async getUploadConstraints(): Promise<ApiResponse<{
    max_file_size: number
    max_files_per_upload: number
    allowed_types: string[]
    allowed_extensions: string[]
    max_total_size: number
  }>> {
    return apiClient.get(`${this.basePath}/upload-constraints`)
  }

  // Recent Files
  async getRecentFiles(limit: number = 10): Promise<ApiResponse<FileAttachment[]>> {
    return apiClient.get(`${this.basePath}/recent`, { limit })
  }

  async getRecentlyDeleted(limit: number = 10): Promise<ApiResponse<Array<{
    file: FileAttachment
    deleted_at: string
    can_restore: boolean
  }>>> {
    return apiClient.get(`${this.basePath}/recently-deleted`, { limit })
  }

  async restoreFile(id: string): Promise<ApiResponse<FileAttachment>> {
    return apiClient.post(`${this.basePath}/${id}/restore`)
  }
}

// Create and export service instance
export const fileService = new FileService() 